import { Injectable } from "@angular/core";

import { combineLatest, forkJoin, noop, Observable, of } from "rxjs";
import { catchError, map, mapTo, share, switchMap } from "rxjs/operators";

import { executeWithProgress } from "@dffedb/rxjs";
import { BrugerHttpClient, InstallationHttpClient, ParametreForAktuelleInstallation } from "@e-forsyning/common/http";
import { InstallationKey } from "@e-forsyning/common/installation";

import { erNoteListeItemOpgave, udtraekOpgaveDokumentnr } from "../filtrer-opgaver-fra-note-liste";
import { harOpgavetype, laesOpgavetype } from "../laes-opgavetype";
import { DbNote } from "../model/db-note";
import { DbNoteListeItem } from "../model/db-note-liste-item";
import { Note, NoteBruger, NoteHenvendelsestype, NoteId, NoteListeItem, NoteOmraade, NoteOpgavestyringStatus } from "../model/note.model";
import { NoteDbMapperService } from "./note-db-mapper.service";

@Injectable({ providedIn: "root" })
export class NoteRepository {
    private omraader: NoteOmraade[] = [
        {
            key: 101,
            value: "Forbruger"
        },
        {
            key: 102,
            value: "Aconto"
        },
        {
            key: 103,
            value: "Installation"
        }
    ];

    constructor(
        private readonly http: InstallationHttpClient,
        private readonly brugerHttp: BrugerHttpClient,
        private readonly noteDbMapper: NoteDbMapperService
    ) {}

    public hentNoterForInstallation(): Observable<NoteListeItem[]> {
        return forkJoin({
            noteliste: this.http.get("api/installationsnoter", ParametreForAktuelleInstallation.true),
            opgaver: this.http.get("api/hentOpgaver").pipe(catchError(() => of({ opgaver: [] })))
        }).pipe(
            map(({ noteliste, opgaver }) => {
                const opgaveIds = udtraekOpgaveDokumentnr(opgaver);
                const raaNoter = ((noteliste?.noteliste as DbNoteListeItem[]) || []).filter((d) => !erNoteListeItemOpgave(d, opgaveIds));
                return raaNoter.map((d) => this.dbToNoteListeItem(d));
            }),
            switchMap((noter) => this.fjernNoterMedOpgavetype(noter)),
            map((noter) => noter.sort((a, b) => b.noteId - a.noteId))
        );
    }

    public hentBrugere(): Observable<NoteBruger[]> {
        return this.http.get("api/fofbrugere", ParametreForAktuelleInstallation.false).pipe(
            share(),
            map((data: any) =>
                data.fofbruger.map((b: any) => ({
                    key: b.initialer,
                    value: `${b.navn} (${b.initialer})`
                }))
            )
        );
    }

    public hentOmraader(): Observable<NoteOmraade[]> {
        return of(this.omraader);
    }

    public hentHenvendelsestyper(): Observable<NoteHenvendelsestype[]> {
        return this.http.get("api/henvendelsestyper").pipe(
            share(),
            map((data: any) => this.noteDbMapper.dbToHenvendelsestyper(data.henvendelsestyper))
        );
    }

    public hentOpgavestyringStatusser(): Observable<NoteOpgavestyringStatus[]> {
        return this.http.get("api/notetilstande").pipe(
            share(),
            map((data: any) => this.noteDbMapper.dbToOpgavestyringStatusser(data.tilstand))
        );
    }

    public hentInstallationKeyForNoteId(noteId: number): Observable<InstallationKey> {
        return this.brugerHttp.get<InstallationKey>(`api/GetInstallationKeyForNoteId?noteid=${noteId}`);
    }

    public hentNote(note: NoteListeItem): Observable<Note> {
        // er det note på installation 0 så starter teksten med (0)
        let aktuelInstallation = ParametreForAktuelleInstallation.true;
        if (note.overskrift.startsWith("(0)")) {
            // lidt et hack. Men kan ikke se om det er en installation 0 note
            aktuelInstallation = ParametreForAktuelleInstallation.udenInstallationsNr; //noter hentes uden installationnr,
        }
        const result = combineLatest([
            this.http.get(`api/installationsnote/dokumentid/${note.noteId}/omraade/${note.omraadeId}`, aktuelInstallation),
            this.http.getUrl("api/notebilag/dokumentid/[dokumentid]/omraade/[omraadeid]", aktuelInstallation)
        ]).pipe(
            map((data) => {
                const dbNote: DbNote = (data[0] as any).note[0];
                const bilagUrlTemplate: string = data[1];
                return this.noteDbMapper.dbToNote(dbNote, bilagUrlTemplate);
            })
        );
        return result;
    }

    public gemNote(note: Note, onProgress: (value: number) => void = noop): Promise<Note> {
        const dbNote = this.noteDbMapper.noteToDb(note);
        return executeWithProgress(
            () =>
                this.http.post<{ noteId: number }>("api/gemnote2", dbNote, ParametreForAktuelleInstallation.true, {
                    observe: "events",
                    reportProgress: true
                }),
            (n) =>
                this.hentNote({
                    noteId: n.noteId,
                    omraadeId: note.omraadeId,
                    antalBilag: note.antalBilag,
                    dato: note.dato,
                    fullyLoaded: note.fullyLoaded,
                    overskrift: note.overskrift
                }),
            onProgress
        );
    }

    public sletNote(note: NoteId): Promise<void> {
        return this.http
            .postWithStringResult(`api/sletnote/dokumentid/${note.noteId}`, null, ParametreForAktuelleInstallation.true)
            .pipe(mapTo(null))
            .toPromise();
    }

    public hentBilagUrlTemplate(): Observable<string> {
        return this.http.getUrl("api/notebilag/dokumentid/[dokumentid]/omraade/[omraadeid]", ParametreForAktuelleInstallation.true);
    }

    /**
     * `api/installationsnoter` sender typisk ikke opgavetype. Tjek detaljer på
     * resterende poster, så opgaver ikke vises på /noter og først forsvinder ved klik.
     */
    private fjernNoterMedOpgavetype(noter: NoteListeItem[]): Observable<NoteListeItem[]> {
        if (noter.length === 0) {
            return of([]);
        }

        return forkJoin(noter.map((note) => this.erNoteOpgave(note).pipe(map((erOpgave) => ({ note, erOpgave }))))).pipe(
            map((rows) => rows.filter((row) => !row.erOpgave).map((row) => row.note))
        );
    }

    private erNoteOpgave(note: NoteListeItem): Observable<boolean> {
        let aktuelInstallation = ParametreForAktuelleInstallation.true;
        if (note.overskrift?.startsWith("(0)")) {
            aktuelInstallation = ParametreForAktuelleInstallation.udenInstallationsNr;
        }

        return this.http.get(`api/installationsnote/dokumentid/${note.noteId}/omraade/${note.omraadeId}`, aktuelInstallation).pipe(
            map((data: any) => {
                const dbNote = Array.isArray(data?.note) ? data.note[0] : data?.note;
                return harOpgavetype(dbNote);
            }),
            catchError(() => of(false))
        );
    }

    private dbToNoteListeItem(dbNote: DbNoteListeItem): NoteListeItem {
        const result: NoteListeItem = {
            antalBilag: parseInt(dbNote.antalbilag, 10),
            dato: dbToDate(dbNote.dato),
            fullyLoaded: false,
            noteId: parseInt(dbNote.id, 10),
            omraadeId: parseInt(dbNote.omraade, 10),
            overskrift: dbNote.overskrift,
            opgaveType: laesOpgavetype(dbNote)
        };
        return result;
    }
}

function dbToDate(value: string): Date {
    if (!value) {
        return null;
    }

    const split = value.split("-").map((s) => parseInt(s, 10));
    return new Date(split[2], split[1] - 1, split[0]);
}
